import { GuestJourneyRetentionVisit } from 'api/reports-service/guest-journey-reports.types';

/**
 * The BE sends `conversionFromLastVisitPct` as an integer percent (e.g. 86);
 * the FE works in 0–1 fractions everywhere downstream (see the API
 * reconciliation doc §2). Divides each row. The live tab applies this at the
 * endpoint boundary (`transformResponse`); the fixture-backed locked preview
 * applies it directly since it bypasses the query.
 */
export const toConversionFractions = (
  visits?: GuestJourneyRetentionVisit[],
): GuestJourneyRetentionVisit[] =>
  Array.isArray(visits)
    ? visits.map(visit => ({
        ...visit,
        // Preserve null (no data) — dividing would coerce it to 0.
        conversionFromLastVisitPct:
          visit.conversionFromLastVisitPct == null
            ? null
            : visit.conversionFromLastVisitPct / 100,
      }))
    : [];

/**
 * View row for the retention curve: the BE visit row plus the FE-derived
 * cumulative cohort share. The BE doesn't provide `conversionFromSignup`
 * (see the API reconciliation doc §5), so the FE computes it as the running
 * product of the per-visit conversions.
 */
export type RetentionVisitViewRow = GuestJourneyRetentionVisit & {
  /**
   * Cumulative share of the original signup cohort still active here (0–1).
   * `null` once a visit with no data (null conversion) is reached — the running
   * product can't continue past a gap, so this and every later visit are null.
   */
  conversionFromSignup: number | null;
};

export const withConversionFromSignup = (
  visits?: GuestJourneyRetentionVisit[],
): RetentionVisitViewRow[] => {
  // Guard non-array payloads (still-loading data, stale mock worker, error
  // bodies) — render an empty curve instead of crashing.
  if (!Array.isArray(visits)) return [];
  let cumulative: number | null = 1;
  return visits.map(visit => {
    // A null conversion (no data) breaks the running product; once broken it
    // stays null for every later visit.
    if (visit.conversionFromLastVisitPct == null) {
      cumulative = null;
    } else if (cumulative != null) {
      cumulative *= visit.conversionFromLastVisitPct;
    }
    return {
      ...visit,
      conversionFromSignup:
        cumulative == null ? null : Math.round(cumulative * 10000) / 10000,
    };
  });
};

/** Visit numbers in the critical retention window (drives the callout). */
export const criticalVisitNumbers = (
  visits?: GuestJourneyRetentionVisit[],
): number[] =>
  Array.isArray(visits)
    ? visits.filter(visit => visit.critical).map(visit => visit.visitNumber)
    : [];
